import { getProductsChanged, getPosPriceRules } from 'utils/apis/posCatalog'
import { META_KEYS } from './constants'
import { clearCatalog, getLocalDb } from './client'
import { getMeta, getMetaNumber, setMeta } from './repositories/meta'
import { countProducts, deleteProductsByIds, upsertProducts } from './repositories/products'
import { replacePriceRules } from './repositories/priceRules'
import { bumpCatalogVersion } from './catalogVersion'
import { isBrowserOnline } from './network'

/** Langkah sinkronisasi untuk progress UI (gate & modal). */
export const SYNC_STEPS = ['products', 'price_rules', 'done']

/** Ambil array dari beberapa bentuk envelope BE (array langsung / paginated {data:[]}). */
export const asArray = (x) => {
  if (Array.isArray(x)) {
    return x
  }
  if (Array.isArray(x?.data)) {
    return x.data
  }
  return []
}

let inFlight = null

const runSync = async (endpoint, onProgress) => {
  if (!endpoint) {
    return { status: 'skipped', reason: 'no-endpoint' }
  }
  if (!isBrowserOnline()) {
    return { status: 'skipped', reason: 'offline' }
  }

  await getLocalDb()

  // Ganti tenant → clear + full resync supaya katalog tenant lama tak tercampur.
  const scope = await getMeta(META_KEYS.scopeEndpoint)
  if (scope !== endpoint) {
    await clearCatalog()
    await setMeta(META_KEYS.scopeEndpoint, endpoint)
  }

  const cursor = await getMetaNumber(META_KEYS.productsSyncedAt)
  const isFull = cursor == null || (await countProducts()) === 0

  // 1) Produk (delta; tanpa cursor untuk full sync).
  onProgress?.('products')
  const prodRes = await getProductsChanged(isFull ? null : cursor)
  const prodBody = prodRes?.data?.data ?? {}
  await upsertProducts(asArray(prodBody.updated))
  await deleteProductsByIds(
    asArray(prodBody.deleted).map((d) => d?.id ?? d?.finance_account_id ?? d),
  )
  if (prodBody.synced_at != null) {
    await setMeta(META_KEYS.productsSyncedAt, String(prodBody.synced_at))
  }

  // 2) Price rule (replace-all). Kategori tidak disinkron: webpos membacanya dari inits.
  onProgress?.('price_rules')
  const ruleRes = await getPosPriceRules()
  await replacePriceRules(asArray(ruleRes?.data?.data))

  await setMeta(META_KEYS.lastSyncedAt, String(Date.now()))
  bumpCatalogVersion()
  onProgress?.('done')

  return { status: 'ok', fullSync: isFull }
}

/**
 * Sinkronisasi turun katalog (produk delta + price rule replace-all). Single-flight:
 * pemanggilan tumpang-tindih memakai promise yang sama. Scope per-tenant via
 * `endpoint`. `onProgress(step)` melaporkan langkah yang sedang berjalan.
 * Reject bila request/penyimpanan gagal (pemanggil yang menampilkan errornya).
 */
export const syncCatalog = (endpoint, onProgress) => {
  if (inFlight) {
    return inFlight
  }
  inFlight = runSync(endpoint, onProgress).finally(() => {
    inFlight = null
  })
  return inFlight
}

/** Waktu sukses sync katalog terakhir (ms) atau null. */
export const getLastCatalogSyncedAt = () => getMetaNumber(META_KEYS.lastSyncedAt)
